import { useEffect, useMemo } from 'react';

import { DictTypes } from '@/constants';
import { generateRandomSixDiginCode } from '@/services';
import { safeKey } from '@/services/helpers/dict';
import { useAppDispatch, useAppSelector } from '@/store';
import { getAttachedFilesByReqId } from '@/store/financingStep.slice';
import { setClientInfo, setLoanData } from '@/store/newOrder.slice';
import type { TClientInfoStore } from '@/types/store';

import useAutoCredRequestId from './useAutoCredRequestId';
import useRefreshOrderData from './useRefreshOrderData';

const useGetOrderData = () => {
  const dispatch = useAppDispatch();
  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp, shouldUpdateRequest } = useAppSelector(
    (state) => state.newOrder
  );

  const { requestId } = useAutoCredRequestId();

  const { refreshOrderData, isLoading } = useRefreshOrderData({
    autoFetch: false,
    requestId
  });

  const personalInfoString = useMemo(
    () => JSON.stringify(orderApp?.personalInfo),
    [orderApp]
  );

  useEffect(() => {
    if ((!orderApp?.uuid || shouldUpdateRequest) && requestId && dicts) {
      refreshOrderData(requestId);
    }
  }, [dicts, requestId, orderApp?.uuid, refreshOrderData, shouldUpdateRequest]);

  useEffect(() => {
    if (orderApp?.uuid) {
      const markModelYear = orderApp.productInfo.product.markModelYear;
      const usedCar = orderApp.productInfo.product.usedCar;

      const brandToFind =
        dicts?.[DictTypes.Brands]?.getBy.code[safeKey(markModelYear?.brand)];
      const modelToFind =
        dicts?.[DictTypes.Models]?.getBy.code[safeKey(markModelYear?.model)];
      const equipment =
        dicts?.[DictTypes.Equipments]?.getBy.uuid[
          safeKey(markModelYear?.equipment)
        ];

      const carInfo = {
        brand: brandToFind?.name || usedCar?.brand_name || '',
        model: modelToFind?.name || usedCar?.model_name || '',
        year: markModelYear?.year || usedCar?.year || ''
      };
      dispatch(
        setLoanData({
          amount: orderApp.productInfo.product.costCar.replace(/\s/g, ''),
          auto: `${carInfo.brand} ${carInfo.model}${equipment ? ` ${equipment.name}` : ''}, ${
            carInfo.year
          }`
        })
      );
    }
  }, [orderApp?.uuid]);

  useEffect(() => {
    if (!orderApp) return;
    const clientInfo: TClientInfoStore = {
      id: orderApp.code || generateRandomSixDiginCode(),
      lastName: orderApp.personalInfo.lastName,
      firstName: orderApp.personalInfo.firstName,
      middleName: orderApp.personalInfo.middleName,
      iin: orderApp.personalInfo.clientIin || '',
      telNum:
        orderApp.personalInfo.phoneNumber || orderApp.personalInfo.workTelNumber
    };
    dispatch(setClientInfo(clientInfo));

    return () => {
      dispatch(setClientInfo(null));
    };
  }, [personalInfoString]);

  useEffect(() => {
    if (orderApp?.uuid) {
      dispatch(getAttachedFilesByReqId(requestId));
    }
  }, [orderApp?.uuid]);

  return { isLoading: isLoading || !orderApp?.uuid };
};

export default useGetOrderData;
